import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { LogOut, Check, X, ExternalLink, RefreshCw, UserCheck, Wallet, Gift, Undo2, Settings, BarChart3 } from "lucide-react";

// PWA isolada — só o essencial para o admin aprovar em movimento, no telemóvel.
// Manifest/ícone próprios (scope /admin), injectados só enquanto esta página
// está montada — nunca afecta o site principal.
function usePainelManifest() {
  useEffect(() => {
    // Só um <link rel="manifest"> deve estar activo de cada vez — o do site
    // (index.html, scope "/") sai temporariamente para o do painel (scope
    // "/admin") não competir com ele durante o "Adicionar ao ecrã principal".
    const siteManifest = document.getElementById("site-manifest");
    siteManifest?.remove();

    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = "/manifest-admin.json";
    document.head.appendChild(link);
    const appleCapable = document.createElement("meta");
    appleCapable.name = "apple-mobile-web-app-capable";
    appleCapable.content = "yes";
    document.head.appendChild(appleCapable);
    const appleTitle = document.createElement("meta");
    appleTitle.name = "apple-mobile-web-app-title";
    appleTitle.content = "Painel Gaia";
    document.head.appendChild(appleTitle);
    const appleIcon = document.createElement("link");
    appleIcon.rel = "apple-touch-icon";
    appleIcon.href = "/icons/painel-192.png";
    document.head.appendChild(appleIcon);
    return () => {
      [link, appleCapable, appleTitle, appleIcon].forEach(el => el.remove());
      if (siteManifest) document.head.appendChild(siteManifest);
    };
  }, []);
}

const TABS = [
  { id: "registos", label: "Registos", icon: UserCheck },
  { id: "pagamentos", label: "Pagamentos", icon: Wallet },
  { id: "premios", label: "Prémios", icon: Gift },
  { id: "resgates", label: "Resgates", icon: Undo2 },
  { id: "definicoes", label: "Definições", icon: Settings },
  { id: "stats", label: "Stats", icon: BarChart3 },
];

export default function Painel() {
  usePainelManifest();
  const { user, logout, loading } = useAuth();
  const [tab, setTab] = useState("registos");

  if (loading) return <main className="min-h-screen flex items-center justify-center bg-[color:var(--bg)] text-gold">A carregar…</main>;
  if (!user || !user.is_admin) return <PainelLogin />;

  return (
    <main className="min-h-screen bg-[color:var(--bg)]">
      <div className="sticky top-0 z-10 backdrop-blur-xl" style={{ background: "rgba(10,16,13,0.92)", borderBottom: "1px solid var(--border)" }}>
        <div className="flex items-center justify-between px-4 pt-4 pb-2 max-w-lg md:max-w-4xl lg:max-w-6xl mx-auto">
          <h1 className="font-display text-2xl text-gold">Painel Gaia</h1>
          <button onClick={logout} className="text-[color:var(--text-2)] hover:text-red-400 flex items-center gap-1 text-sm" data-testid="painel-logout">
            <LogOut size={16}/> Sair
          </button>
        </div>
        <nav className="flex gap-1 px-2 pb-2 overflow-x-auto max-w-lg md:max-w-4xl lg:max-w-6xl mx-auto md:justify-center" data-testid="painel-navbar">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`painel-tab-${t.id}`}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-md text-xs shrink-0 ${tab === t.id ? "bg-gold text-[color:var(--bg)]" : "text-[color:var(--text-2)] hover:text-gold"}`}>
              <t.icon size={16}/> {t.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="px-4 py-6 max-w-lg md:max-w-4xl lg:max-w-6xl mx-auto">
        {tab === "registos" && <RegistosSection />}
        {tab === "pagamentos" && <PagamentosSection />}
        {tab === "premios" && <PremiosSection />}
        {tab === "resgates" && <ResgatesSection />}
        {tab === "definicoes" && <DefinicoesSection />}
        {tab === "stats" && <StatsSection />}
      </div>
    </main>
  );
}

function PainelLogin() {
  const { setUser } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // "admin" (username hardcoded, sem email) ou email — decidido pelo "@".
      const body = identifier.includes("@")
        ? { email: identifier, password }
        : { username: identifier, password };
      const r = await api.post("/auth/login", body);
      if (!r.data.is_admin) {
        await api.post("/auth/logout");
        toast.error("Esta conta não tem acesso de admin");
        return;
      }
      setUser(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login inválido");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-[color:var(--bg)] px-4">
      <form onSubmit={submit} className="glass p-8 w-full max-w-sm space-y-4">
        <h1 className="font-display text-3xl text-gold text-center mb-2">Painel Gaia</h1>
        <p className="text-center text-[color:var(--text-2)] text-sm mb-4">Acesso reservado a administradores</p>
        <input className="input" type="text" placeholder="User (admin ou email)" value={identifier}
          onChange={e => setIdentifier(e.target.value)} required autoFocus data-testid="painel-login-email" />
        <input className="input" type="password" placeholder="Password" value={password}
          onChange={e => setPassword(e.target.value)} required data-testid="painel-login-password" />
        <button className="btn-primary w-full" disabled={busy} data-testid="painel-login-submit">
          {busy ? "…" : "Entrar"}
        </button>
      </form>
    </main>
  );
}

function RegistosSection() {
  const [items, setItems] = useState([]);
  const load = async () => {
    const r = await api.get("/admin/users", { params: { status: "pending" } });
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => { await api.post(`/admin/users/${id}/approve`); toast.success("Registo aprovado"); load(); };
  const reject = async (id) => { if (confirm("Recusar este registo?")) { await api.post(`/admin/users/${id}/reject`); load(); } };

  return (
    <Section title={`Registos pendentes (${items.length})`} onRefresh={load}>
      {items.length === 0 && <Empty text="Sem registos por aprovar." />}
      <div className="sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-3">
      {items.map(u => (
        <div key={u.user_id} className="glass p-4 mb-3 sm:mb-0" data-testid={`painel-user-${u.user_id}`}>
          <div className="font-display text-xl">{u.name}</div>
          <div className="text-sm text-[color:var(--text-2)]">{u.email}</div>
          <div className="text-sm text-[color:var(--text-2)] mb-3">{u.phone}</div>
          <div className="flex gap-2">
            <button onClick={() => approve(u.user_id)} className="btn-primary flex-1 flex items-center justify-center gap-1 !py-2" data-testid={`painel-approve-user-${u.user_id}`}>
              <Check size={16}/> Aprovar
            </button>
            <button onClick={() => reject(u.user_id)} className="flex-1 flex items-center justify-center gap-1 !py-2 rounded-md border border-red-500 text-red-400">
              <X size={16}/> Recusar
            </button>
          </div>
        </div>
      ))}
      </div>
    </Section>
  );
}

function PagamentosSection() {
  const [items, setItems] = useState([]);
  const load = async () => {
    const r = await api.get("/admin/orders", { params: { status: "pending" } });
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => { await api.post(`/admin/orders/${id}/approve`); toast.success("Pagamento aprovado"); load(); };
  const cancel = async (id) => { if (confirm("Cancelar pedido?")) { await api.post(`/admin/orders/${id}/cancel`); load(); } };

  return (
    <Section title={`Pagamentos pendentes (${items.length})`} onRefresh={load}>
      {items.length === 0 && <Empty text="Sem pagamentos por aprovar." />}
      <div className="sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-3">
      {items.map(o => (
        <div key={o.order_id} className="glass p-4 mb-3 sm:mb-0" data-testid={`painel-order-${o.order_id}`}>
          <div className="flex justify-between items-start">
            <div>
              <div className="font-display text-xl">{o.user_name}</div>
              <div className="text-sm text-[color:var(--text-2)]">{o.user_email}</div>
            </div>
            <div className="text-right">
              <div className="text-gold font-display text-2xl">{o.total_eur.toFixed(2)}€</div>
              <div className="text-xs text-[color:var(--text-3)] uppercase">{o.quantity}× · {o.payment_method}</div>
            </div>
          </div>
          {o.proof_file
            ? <a href={`${API}/orders/${o.order_id}/proof`} target="_blank" rel="noreferrer"
                className="text-gold underline text-sm flex items-center gap-1 mt-2" data-testid={`painel-proof-${o.order_id}`}>
                <ExternalLink size={14}/> Ver comprovativo
              </a>
            : <p className="text-xs text-[color:var(--text-3)] mt-2">Sem comprovativo anexado ainda.</p>}
          <div className="flex gap-2 mt-3">
            <button onClick={() => approve(o.order_id)} className="btn-primary flex-1 flex items-center justify-center gap-1 !py-2" data-testid={`painel-approve-order-${o.order_id}`}>
              <Check size={16}/> Aprovar
            </button>
            <button onClick={() => cancel(o.order_id)} className="flex-1 flex items-center justify-center gap-1 !py-2 rounded-md border border-red-500 text-red-400">
              <X size={16}/> Cancelar
            </button>
          </div>
        </div>
      ))}
      </div>
    </Section>
  );
}

const EMPTY_PRIZE = { name: "", description: "", price_eur: 5, image_url: "", stock: -1, active: true };

function PremiosSection() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_PRIZE);
  const [editingId, setEditingId] = useState(null); // null = a criar; senão prize_id em edição
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const load = async () => { const r = await api.get("/admin/prizes"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_PRIZE); setEditingId(null); setShowForm(true); };
  const openEdit = (p) => {
    setForm({ name: p.name, description: p.description, price_eur: p.price_eur, image_url: p.image_url, stock: p.stock, active: p.active });
    setEditingId(p.prize_id);
    setShowForm(true);
  };
  const closeForm = () => setShowForm(false);

  const save = async () => {
    try {
      if (editingId) {
        await api.put(`/admin/prizes/${editingId}`, form);
        toast.success("Guardado");
      } else {
        await api.post("/admin/prizes", form);
        toast.success("Criado");
      }
      setShowForm(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingId) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const r = await api.post(`/admin/prizes/${editingId}/image`, body);
      setForm(f => ({ ...f, image_url: r.data.image_url }));
      toast.success("Imagem substituída");
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro no upload"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const togg = async (p) => { await api.put(`/admin/prizes/${p.prize_id}`, { ...p, active: !p.active }); load(); };
  const del = async (id) => { if (confirm("Apagar prémio?")) { await api.delete(`/admin/prizes/${id}`); load(); } };

  return (
    <Section title={`Prémios (${items.length})`} onRefresh={load}>
      {!showForm && (
        <button onClick={openNew} className="btn-secondary w-full mb-3" data-testid="painel-prize-new-toggle">
          + Novo prémio
        </button>
      )}
      {showForm && (
        <div className="glass p-4 mb-4 space-y-2">
          <div className="text-xs uppercase text-[color:var(--text-3)]">{editingId ? "Editar prémio" : "Novo prémio"}</div>
          <input className="input" placeholder="Nome" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="painel-prize-name" />
          <textarea className="input" placeholder="Descrição" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          <input className="input" type="number" step="0.5" placeholder="Preço €" value={form.price_eur} onChange={e => setForm({ ...form, price_eur: parseFloat(e.target.value) })} data-testid="painel-prize-price" />
          <input className="input" placeholder="URL imagem" value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} />
          {editingId && (
            <div className="flex items-center gap-3">
              {form.image_url && <img src={form.image_url} alt="" className="w-12 h-12 rounded object-cover" />}
              <label className="btn-secondary flex-1 text-center cursor-pointer !py-2 text-sm" data-testid="painel-prize-image-upload-label">
                {uploading ? "A enviar…" : "Substituir imagem…"}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                  onChange={uploadImage} disabled={uploading} data-testid="painel-prize-image-upload-input" />
              </label>
            </div>
          )}
          <input className="input" type="number" placeholder="Stock (-1 ilimitado)" value={form.stock} onChange={e => setForm({ ...form, stock: parseInt(e.target.value) })} />
          <div className="flex gap-2">
            <button className="btn-primary flex-1" onClick={save} data-testid="painel-prize-save-btn">{editingId ? "Guardar" : "Criar"}</button>
            <button className="flex-1 rounded-md border border-[color:var(--border)]" onClick={closeForm}>Cancelar</button>
          </div>
        </div>
      )}
      {items.length === 0 && <Empty text="Sem prémios no catálogo." />}
      <div className="sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-3">
      {items.map(p => (
        <div key={p.prize_id} className="glass p-4 mb-3 sm:mb-0 flex gap-3" data-testid={`painel-prize-${p.prize_id}`}>
          {p.image_url && <img src={p.image_url} className="w-14 h-14 rounded object-cover shrink-0" alt="" />}
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start gap-2">
              <div className="font-display text-lg truncate">{p.name}</div>
              <div className="text-gold shrink-0">{p.price_eur.toFixed(2)}€</div>
            </div>
            <div className="flex gap-3 mt-2 text-xs flex-wrap">
              <button onClick={() => openEdit(p)} className="underline text-gold" data-testid={`painel-prize-edit-${p.prize_id}`}>Editar</button>
              <button onClick={() => togg(p)} className="underline text-[color:var(--text-2)]">{p.active ? "Desativar" : "Ativar"}</button>
              <button onClick={() => del(p.prize_id)} className="underline text-red-400">Apagar</button>
              <span className={`ml-auto uppercase ${p.active ? "text-gold" : "text-[color:var(--text-3)]"}`}>{p.active ? "activo" : "inactivo"}</span>
            </div>
          </div>
        </div>
      ))}
      </div>
    </Section>
  );
}

function ResgatesSection() {
  const [items, setItems] = useState([]);
  const load = async () => { const r = await api.get("/admin/redemptions"); setItems(r.data); };
  useEffect(() => { load(); }, []);
  const fulfill = async (id) => { await api.post(`/admin/redemptions/${id}/fulfill`); load(); };
  const confirmShipping = async (id) => { await api.post(`/admin/redemptions/${id}/confirm-shipping`); load(); };

  return (
    <Section title={`Resgates (${items.length})`} onRefresh={load}>
      {items.length === 0 && <Empty text="Sem resgates registados." />}
      <div className="sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-3">
      {items.map(r => {
        const sp = r.shipping_payment;
        const shippingBlocking = sp && sp.status !== "paid";
        return (
        <div key={r.redemption_id} className="glass p-4 mb-3 sm:mb-0" data-testid={`painel-redemption-${r.redemption_id}`}>
          <div className="flex justify-between">
            <div>
              <div className="font-display text-lg">{r.prize_name}</div>
              <div className="text-sm text-[color:var(--text-2)]">{r.user_email}</div>
            </div>
            <div className="text-gold">{(r.total_charged ?? r.prize_price).toFixed(2)}€</div>
          </div>
          <div className="text-xs text-[color:var(--text-2)] mt-1">
            {r.delivery_method === "shipping" ? (
              <>Envio → {r.address?.name}, {r.address?.street}, {r.address?.postal_code} {r.address?.city} · {r.address?.phone}</>
            ) : "Levantamento presencial"}
          </div>
          {sp && (
            <div className="text-xs mt-1 flex items-center gap-2">
              <span className={sp.status === "paid" ? "text-gold" : "text-[color:var(--text-2)]"}>
                Portes ({sp.amount.toFixed(2)}€ via {sp.method}): {sp.status === "paid" ? "confirmados" : (sp.proof_file ? "comprovativo enviado" : "por pagar")}
              </span>
              {sp.status !== "paid" && sp.proof_file && (
                <button onClick={() => confirmShipping(r.redemption_id)} className="btn-secondary !py-0.5 !px-2 text-xs" data-testid={`painel-confirm-shipping-${r.redemption_id}`}>
                  Confirmar
                </button>
              )}
              {sp.status !== "paid" && sp.proof_file && (
                <a href={`${api.defaults.baseURL}/redemptions/${r.redemption_id}/shipping-proof`} target="_blank" rel="noreferrer" className="underline">ver comprovativo</a>
              )}
            </div>
          )}
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs uppercase text-[color:var(--text-3)]">{r.status}</span>
            {r.status === "pending" && (
              <button onClick={() => fulfill(r.redemption_id)} disabled={shippingBlocking}
                className="btn-primary text-xs !py-1 !px-3 disabled:opacity-50" data-testid={`painel-fulfill-${r.redemption_id}`}>
                {shippingBlocking ? "Aguarda portes" : "Entregar"}
              </button>
            )}
          </div>
        </div>
        );
      })}
      </div>
    </Section>
  );
}

function DefinicoesSection() {
  const [s, setS] = useState(null);
  const load = async () => { const r = await api.get("/admin/settings"); setS(r.data); };
  useEffect(() => { load(); }, []);
  if (!s) return <Empty text="A carregar…" />;

  const save = async () => {
    try {
      await api.put("/admin/settings", {
        card_price_eur: parseFloat(s.card_price_eur),
        roulette_values: s.roulette_values.map(v => parseInt(v)),
        iban: s.iban, mbway_phone: s.mbway_phone, paypal_email: s.paypal_email,
        crypto_wallets: s.crypto_wallets, admin_emails: s.admin_emails,
      });
      toast.success("Guardado");
      load();
    } catch { toast.error("Erro"); }
  };

  const resetAll = async () => {
    if (!confirm("Isto apaga TODAS as cartas e pedidos, e zera o saldo de TODOS os utilizadores. É irreversível. Continuar?")) return;
    if (!confirm("Confirma outra vez: tens a certeza absoluta?")) return;
    try { await api.post("/admin/reset-all"); toast.success("Reset feito"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const factoryReset = async () => {
    if (!confirm("Isto apaga TODAS as cartas, pedidos, resgates E TODAS AS CONTAS (só a tua conta admin sobrevive, com saldo a 0 e login por username \"admin\"). Irreversível. Continuar?")) return;
    if (prompt('Escreve APAGAR TUDO para confirmar:') !== "APAGAR TUDO") { toast.error("Cancelado"); return; }
    try {
      await api.post("/admin/factory-reset");
      toast.success('Reset total feito. A partir de agora entra com username "admin".');
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <Section title="Definições">
      <div className="glass p-4 space-y-3 max-w-2xl">
        <label className="block"><div className="text-xs uppercase text-[color:var(--text-3)] mb-1">Preço por carta (€)</div>
          <input className="input" type="number" step="0.5" value={s.card_price_eur} onChange={e => setS({ ...s, card_price_eur: e.target.value })} /></label>
        <label className="block"><div className="text-xs uppercase text-[color:var(--text-3)] mb-1">Valores da roleta (separados por vírgula)</div>
          <input className="input" value={(s.roulette_values || []).join(",")} onChange={e => setS({ ...s, roulette_values: e.target.value.split(",").map(v => v.trim()).filter(Boolean) })} /></label>
        <label className="block"><div className="text-xs uppercase text-[color:var(--text-3)] mb-1">IBAN</div>
          <input className="input" value={s.iban || ""} onChange={e => setS({ ...s, iban: e.target.value })} /></label>
        <label className="block"><div className="text-xs uppercase text-[color:var(--text-3)] mb-1">MBWay nº</div>
          <input className="input" value={s.mbway_phone || ""} onChange={e => setS({ ...s, mbway_phone: e.target.value })} /></label>
        <label className="block"><div className="text-xs uppercase text-[color:var(--text-3)] mb-1">PayPal email</div>
          <input className="input" value={s.paypal_email || ""} onChange={e => setS({ ...s, paypal_email: e.target.value })} /></label>
        <label className="block"><div className="text-xs uppercase text-[color:var(--text-3)] mb-1">Crypto wallets (JSON)</div>
          <textarea className="input font-mono text-xs" rows={3} value={JSON.stringify(s.crypto_wallets || {}, null, 2)}
            onChange={e => { try { setS({ ...s, crypto_wallets: JSON.parse(e.target.value) }); } catch {} }} /></label>
        <label className="block"><div className="text-xs uppercase text-[color:var(--text-3)] mb-1">Admins (emails, separados por vírgula)</div>
          <input className="input" value={(s.admin_emails || []).join(",")} onChange={e => setS({ ...s, admin_emails: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })} /></label>
        <button className="btn-primary w-full" onClick={save} data-testid="painel-settings-save">Guardar</button>
      </div>

      <div className="border-t border-red-900/40 pt-4 mt-6 max-w-2xl space-y-3">
        <div className="text-xs uppercase text-red-400 mb-2">Zona de perigo</div>
        <button className="w-full px-4 py-2 rounded-md text-sm border border-red-500 text-red-400" onClick={resetAll} data-testid="painel-reset-all">
          Reset de época — apagar cartas, pedidos e saldos (contas ficam)
        </button>
        <button className="w-full px-4 py-2 rounded-md text-sm bg-red-500 text-white" onClick={factoryReset} data-testid="painel-factory-reset">
          Reset total — apagar tudo, incluindo todas as contas
        </button>
      </div>
    </Section>
  );
}

function StatsSection() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)); }, []);
  if (!stats) return <Empty text="A carregar…" />;

  const tiles = [
    { label: "Users", value: stats.total_users },
    { label: "Registos pendentes", value: stats.pending_users },
    { label: "Cartas", value: stats.total_cards },
    { label: "Receita paga", value: `${(stats.total_revenue_eur || 0).toFixed(2)}€` },
    { label: "Pedidos pagos", value: stats.orders_by_status?.paid?.count || 0 },
    { label: "Pedidos pendentes", value: stats.orders_by_status?.pending?.count || 0 },
  ];

  return (
    <Section title="Estatísticas">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map(t => (
          <div key={t.label} className="glass p-4">
            <div className="text-xs uppercase text-[color:var(--text-3)]">{t.label}</div>
            <div className="font-display text-2xl text-gold">{t.value}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Section({ title, onRefresh, children }) {
  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl">{title}</h2>
        {onRefresh && (
          <button onClick={onRefresh} className="text-[color:var(--text-2)] hover:text-gold" data-testid="painel-refresh">
            <RefreshCw size={16}/>
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function Empty({ text }) {
  return <p className="text-[color:var(--text-3)] text-sm text-center py-6">{text}</p>;
}
